import React, { useState } from 'react';
import { Search, ChevronLeft, ChevronRight, SkipBack, SkipForward, Hash, Filter } from 'lucide-react';
import { CsvData } from '../../types';

interface RecordSearchJumperProps {
  csvData: CsvData | null;
  currentIndex: number;
  onSelectRecord: (index: number) => void;
  className?: string;
}

export const RecordSearchJumper: React.FC<RecordSearchJumperProps> = ({
  csvData,
  currentIndex,
  onSelectRecord,
  className = ''
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [matchingIndices, setMatchingIndices] = useState<number[]>([]);
  const [matchCursor, setMatchCursor] = useState(0);

  if (!csvData || !csvData.rows || csvData.rows.length === 0) {
    return null;
  }

  const totalRecords = csvData.rows.length;

  const handleSearchChange = (val: string) => {
    setSearchTerm(val);
    if (!val.trim()) {
      setMatchingIndices([]);
      setMatchCursor(0);
      return;
    }
    const q = val.toLowerCase().trim();
    const matches: number[] = [];
    csvData.rows.forEach((row, idx) => {
      const matchInRow = Object.values(row).some(v => String(v || '').toLowerCase().includes(q));
      if (matchInRow) matches.push(idx);
    });
    setMatchingIndices(matches);
    setMatchCursor(0);
    if (matches.length > 0) {
      onSelectRecord(matches[0]);
    }
  };

  const handleNextMatch = () => {
    if (matchingIndices.length === 0) return;
    const nextCursor = (matchCursor + 1) % matchingIndices.length;
    setMatchCursor(nextCursor);
    onSelectRecord(matchingIndices[nextCursor]);
  };

  const handlePrevMatch = () => {
    if (matchingIndices.length === 0) return;
    const prevCursor = (matchCursor - 1 + matchingIndices.length) % matchingIndices.length;
    setMatchCursor(prevCursor);
    onSelectRecord(matchingIndices[prevCursor]);
  };

  const handleDirectJump = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseInt(e.target.value, 10);
    if (!isNaN(parsed)) {
      const recordIdx = Math.max(0, Math.min(totalRecords - 1, parsed - 1));
      onSelectRecord(recordIdx);
    }
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 bg-zinc-950 border border-zinc-800 p-1.5 rounded-xl text-xs ${className}`}>
      
      {/* Quick Nav Controls */}
      <div className="flex items-center gap-0.5 bg-zinc-900 border border-zinc-800/80 rounded-lg p-0.5">
        <button
          onClick={() => onSelectRecord(0)}
          disabled={currentIndex === 0}
          className="p-1 text-zinc-400 hover:text-white disabled:opacity-30 rounded cursor-pointer hover:bg-zinc-800"
          title="First Record (1)"
        >
          <SkipBack className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onSelectRecord(Math.max(0, currentIndex - 1))}
          disabled={currentIndex === 0}
          className="p-1 text-zinc-400 hover:text-white disabled:opacity-30 rounded cursor-pointer hover:bg-zinc-800"
          title="Previous Record"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>

        {/* Input Record Number Jumper */}
        <div className="flex items-center gap-1 px-1.5 py-0.5 font-mono text-zinc-300">
          <Hash className="w-3 h-3 text-zinc-500 shrink-0" />
          <input
            type="number"
            min={1}
            max={totalRecords}
            value={currentIndex + 1}
            onChange={handleDirectJump}
            className="w-12 bg-zinc-950 border border-zinc-800 rounded text-center text-xs font-mono font-bold text-emerald-400 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/50 py-0.5"
          />
          <span className="text-zinc-500 font-sans text-[11px]">/ {totalRecords}</span>
        </div>

        <button
          onClick={() => onSelectRecord(Math.min(totalRecords - 1, currentIndex + 1))}
          disabled={currentIndex >= totalRecords - 1}
          className="p-1 text-zinc-400 hover:text-white disabled:opacity-30 rounded cursor-pointer hover:bg-zinc-800"
          title="Next Record"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onSelectRecord(totalRecords - 1)}
          disabled={currentIndex >= totalRecords - 1}
          className="p-1 text-zinc-400 hover:text-white disabled:opacity-30 rounded cursor-pointer hover:bg-zinc-800"
          title={`Last Record (${totalRecords})`}
        >
          <SkipForward className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* CSV Value Search Filter */}
      <div className="flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1 min-w-[160px]">
        <Search className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
        <input
          type="text"
          placeholder="Search CSV row..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="bg-transparent text-xs text-zinc-100 placeholder-zinc-500 outline-none w-28 font-mono"
        />

        {searchTerm && (
          <div className="flex items-center gap-1 text-[10px] font-mono border-l border-zinc-800 pl-1.5 shrink-0">
            {matchingIndices.length > 0 ? (
              <>
                <span className="text-emerald-400 font-bold">{matchCursor + 1}/{matchingIndices.length}</span>
                <button 
                  onClick={handlePrevMatch} 
                  className="p-0.5 hover:text-white text-zinc-400 cursor-pointer"
                  title="Previous match"
                >
                  ▲
                </button>
                <button 
                  onClick={handleNextMatch} 
                  className="p-0.5 hover:text-white text-zinc-400 cursor-pointer"
                  title="Next match"
                >
                  ▼
                </button>
              </>
            ) : (
              <span className="text-red-400">0 matches</span>
            )}
          </div>
        )}
      </div>

    </div>
  );
};
